import React, { useEffect, useMemo, useState } from 'react';
import { MapPinIcon, TruckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AttendanceStatus } from '../../types/event';
import type { EventTravel } from '../../types/travel';
import { PlayerTravelMode, formatTravelTime, playerTravelMode } from '../../types/travel';
import { aboardMyCar, type TravelResponder } from './useTravelResponder';

interface MyCarPanelProps {
  travel: EventTravel;
  responder: TravelResponder;
}

// Mobilon 16px, különben az iOS ránagyít a mezőre
const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const pad = (n: number) => n.toString().padStart(2, '0');

/** datetime-local formátum: egy órával a kezdés előtt. */
const defaultMeetTime = (eventStart: string) => {
  const date = new Date(eventStart);
  date.setHours(date.getHours() - 1);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * A saját autó: csak akkor jelenik meg, ha a szülő jelezte, hogy "Tudok vinni
 * valakit" (vagy már felajánlotta). Itt adja meg, hány gyerek fér be,
 * honnan és mikor indul, és itt ültethet be másokat a csapatból.
 */
const MyCarPanel: React.FC<MyCarPanelProps> = ({ travel, responder }) => {
  const { busy, pendingCarFor, cancelPendingCar, saveCar, deleteCar, setPlayer } = responder;
  const myCar = travel.vehicles.find((v) => v.isMine);
  const aboard = aboardMyCar(travel);
  const [editing, setEditing] = useState(false);
  const [seats, setSeats] = useState(3);
  const [meetingPoint, setMeetingPoint] = useState('');
  const [meetTime, setMeetTime] = useState('');
  const [carNotes, setCarNotes] = useState('');

  const formOpen = editing || (!myCar && pendingCarFor != null);

  // A űrlap mindig a jelenlegi adatokkal nyílik
  useEffect(() => {
    if (!formOpen) return;
    setSeats(myCar?.seats ?? Math.max(3, aboard.length));
    // Az általános "Indulás" helynév csak az időpont hordozója - a mezőbe nem írjuk vissza
    setMeetingPoint(myCar?.stops[0]?.name && myCar.stops[0].name !== 'Indulás' ? myCar.stops[0].name : '');
    setMeetTime(myCar?.stops[0]?.meetTime?.slice(0, 16) ?? (myCar ? '' : defaultMeetTime(travel.eventStart)));
    setCarNotes(myCar?.notes ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen]);

  // Akit még beültethetek: a keretben van, nem mondott nemet, és nem döntöttek róla máshogy
  const candidates = useMemo(() => {
    if (!myCar) return [];
    return travel.roster
      .filter((r) => r.inRoster !== false && r.response !== AttendanceStatus.NotAttending)
      .map((r) => ({ ...r, mode: playerTravelMode(travel, r.playerId) }))
      .filter((r) => r.mode === PlayerTravelMode.None || r.mode === PlayerTravelMode.NeedsRide)
      .sort((a, b) => Number(b.mode === PlayerTravelMode.NeedsRide) - Number(a.mode === PlayerTravelMode.NeedsRide));
  }, [travel, myCar]);

  if (!myCar && !formOpen) return null;

  // A beülő gyereknek is kell hely
  const minSeats = Math.max(1, aboard.length + (myCar ? 0 : pendingCarFor != null ? 1 : 0));
  const seatsValid = seats >= minSeats && seats <= 20;
  const full = myCar ? aboard.length >= myCar.seats : false;

  const submit = async () => {
    const ok = await saveCar({
      seats,
      meetingPoint: meetingPoint.trim() || null,
      meetTime: meetTime ? `${meetTime.slice(0, 16)}:00` : null,
      notes: carNotes.trim() || null,
    });
    if (ok) setEditing(false);
  };

  const cancel = () => {
    setEditing(false);
    cancelPendingCar();
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      {formOpen ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-900">{myCar ? 'Az autód adatai' : 'Kit tudsz vinni?'}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[9rem_1fr_12rem]">
            <div>
              <label htmlFor="ride-seats" className="mb-1 block text-xs font-medium text-gray-600">
                Hány gyerek fér be?
              </label>
              <input
                id="ride-seats"
                type="number"
                min={minSeats}
                max={20}
                value={seats || ''}
                onChange={(e) => setSeats(Number(e.target.value))}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">A sajátodat is beleszámolva.</p>
            </div>
            <div>
              <label htmlFor="ride-point" className="mb-1 block text-xs font-medium text-gray-600">
                Honnan indulsz? (nem kötelező)
              </label>
              <input
                id="ride-point"
                value={meetingPoint}
                onChange={(e) => setMeetingPoint(e.target.value)}
                maxLength={200}
                placeholder="Pl. Tesco parkoló"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="ride-time" className="mb-1 block text-xs font-medium text-gray-600">
                Mikor indulsz?
              </label>
              <input
                id="ride-time"
                type="datetime-local"
                value={meetTime}
                onChange={(e) => setMeetTime(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label htmlFor="ride-notes" className="mb-1 block text-xs font-medium text-gray-600">
              Megjegyzés
            </label>
            <input
              id="ride-notes"
              value={carNotes}
              onChange={(e) => setCarNotes(e.target.value)}
              maxLength={500}
              placeholder="Pl. ülésmagasító van, hazafelé is hozok"
              className={inputClass}
            />
          </div>
          {!seatsValid && (
            <p className="text-xs text-red-600">
              Legalább {minSeats}, legfeljebb 20 hely adható meg.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !seatsValid}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Mentés
            </button>
          </div>
        </div>
      ) : (
        myCar && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <TruckIcon className="h-5 w-5 shrink-0 text-gray-500" />
                  Az autód · {aboard.length} / {myCar.seats} hely
                </p>
                {myCar.stops[0] && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-600">
                    <MapPinIcon className="h-4 w-4 shrink-0" />
                    {myCar.stops[0].name}
                    {myCar.stops[0].meetTime && `, ${formatTravelTime(myCar.stops[0].meetTime)}`}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  disabled={busy}
                  className="rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50"
                >
                  Módosítás
                </button>
                <button
                  type="button"
                  onClick={deleteCar}
                  disabled={busy}
                  className="rounded-md px-2 py-1 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Visszavonom
                </button>
              </div>
            </div>

            {aboard.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {aboard.map((p) => (
                  <li
                    key={p.id}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-0.5 pl-2.5 pr-1 text-sm text-primary-900"
                  >
                    {p.name}
                    {p.playerId && !p.isMine && (
                      <button
                        type="button"
                        onClick={() => setPlayer(p.playerId!, { mode: PlayerTravelMode.NeedsRide }, true)}
                        disabled={busy}
                        aria-label={`${p.name} kivétele az autóból`}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-primary-700 hover:bg-primary-100"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {candidates.length > 0 && (
              <div>
                <label htmlFor="ride-add" className="mb-1 block text-xs font-medium text-gray-600">
                  Kit viszel még?
                </label>
                <select
                  id="ride-add"
                  value=""
                  disabled={busy || full}
                  onChange={(e) => {
                    const playerId = Number(e.target.value);
                    if (playerId) setPlayer(playerId, { mode: PlayerTravelMode.Vehicle, travelVehicleId: myCar.id }, true);
                  }}
                  className={`${inputClass} disabled:bg-gray-50`}
                >
                  <option value="">{full ? 'Az autód megtelt' : 'Válassz a csapatból...'}</option>
                  {candidates.map((c) => (
                    <option key={c.playerId} value={c.playerId}>
                      {c.name}
                      {c.mode === PlayerTravelMode.NeedsRide ? ' – fuvar kell' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">A gyerek szülei értesítést kapnak, hogy te viszed.</p>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default MyCarPanel;
